import {
  Button,
  FileInput,
  Select,
  TextInput,
  Textarea,
  Tooltip,
} from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Loader from '../Common/Loader';
import blankImage from '../../Assets/NoPhoto.jpg';
import { Link, useNavigate, useParams } from 'react-router-dom';
import UserApi from '../../Api/UserApi';
import { User } from '../../Models/User';
import {
  fetchClients,
  fetchExistingEmails,
  fetchStates,
  fetchUserLevels,
} from '../../Actions/CommonFields';
import { useAppDispatch, useAppSelector } from '../../Reducers/store';
import { UserLevel, UserLevelNames } from '../../Models/UserLevel';
import { fetchCurrentUser } from '../../Actions';
import { HiEye, HiInformationCircle } from 'react-icons/hi';
import UserUpdatePassword from './UserUpdatePassword';

export default function EditUserProfile() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { states, userLevels, existingEmails, clients } = useAppSelector(
    (state) => state.commonFields
  );
  const { userModel } = useAppSelector((state) => state.user);
  const [userData, setUserData] = useState<User | null>();
  const [show, setShow] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [emailExists, setEmailExists] = useState<boolean>(false);
  const [formData, setFormData] = useState<User>({
    id: 0,
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    birthdate: null,
    userLevelId: 1,
    isPrimary: false,
    address: '',
    city: '',
    state: '',
    zip: '',
    bio: '',
    clientId: null,
    emailConfirmed: null,

    client: null,
    userLevel: null,
  });
  const [file, setFile] = useState<File | null>();

  const getUser = () => {
    if (id) {
      setIsLoading(true);
      UserApi.getUser(id)
        .then((res) => {
          setUserData(res.data);
          setIsLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
    } else {
      setUserData(null);
    }
  };

  const getReferences = async () => {
    if (
      !states.length ||
      !userLevels.length ||
      !existingEmails.length ||
      !clients?.length
    ) {
      await Promise.all([
        dispatch(fetchUserLevels()),
        dispatch(fetchExistingEmails()),
      ]);
      //   setIsLoading(false);
    } else {
      //   setIsLoading(false);
    }
  };

  useEffect(() => {
    getUser();
  }, [id]);

  useEffect(() => {
    if (userData && userModel && id) {
      if (userModel.userLevelId !== UserLevelNames.Admin) {
        if (userModel.id !== userData.id) {
          toast.error('You do not have access to this page');
          navigate(-1);
        }
      }
    }
    handleSetInitialValues();
  }, [userData]);

  useEffect(() => {
    getReferences();
  }, []);

  const handleSetInitialValues = () => {
    const values = userData
      ? {
          ...userData,
          userLevelId: userData.userLevelId ?? 1,
        }
      : {
          id: 0,
          firstName: '',
          lastName: '',
          emailAddress: '',
          phoneNumber: '',
          birthdate: null,
          userLevelId: 1,
          isPrimary: false,
          address: '',
          city: '',
          state: '',
          zip: '',
          bio: '',
          clientId: null,
          emailConfirmed: null,

          client: null,
          userLevel: null,
        };

    setFormData(values as User);
  };

  const checkEmail = (email: string) => {
    if (
      userData?.emailAddress &&
      email.trim().toLowerCase() !==
        userData?.emailAddress.trim().toLowerCase() &&
      existingEmails.some((x) => x === email.trim().toLowerCase())
    ) {
      setButtonDisabled(true);
      setEmailExists(true);
    } else {
      setButtonDisabled(false);
      setEmailExists(false);
    }
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    if (name === 'emailAddress') {
      checkEmail(value);
    }
  };

  const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    setButtonDisabled(true);
    e.preventDefault();
    if (!formData.clientId) {
      formData.clientId = null;
    }
    try {
      setIsLoading(true);
      return UserApi.addUpdateUser(formData)
        .then((res) => {
          if (res.data.success) {
            toast.success('Saved');
            setIsLoading(false);
            setButtonDisabled(false);
            navigate(`/user/${formData?.id}`);
          } else {
            setButtonDisabled(false);
            toast.error(res.data.message);
          }
        })
        .catch((err) => {
          toast.error('Error: ' + err);
          setIsLoading(false);
          setButtonDisabled(false);
        });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setButtonDisabled(false);
    }
  };

  return (
    <div className='w-full'>
      <div className='w-full p-4'>
        <div className='mx-1 flex items-end'>
          <div className='flex flex-1'>
            <h1 className='text-gray-900 font-bold text-4xl'>
              Edit User Information
            </h1>
          </div>
          <div className='right-2'>
            <Link to={`/user/${userData?.id}`}>
              <Tooltip content='View'>
                <Button className='buttonMain'>
                  <HiEye size={20} />
                </Button>
              </Tooltip>
            </Link>
          </div>
        </div>
      </div>

      <div className='h-[calc(100vh-125px)] overflow-y-auto'>
        <div className='container mx-auto p-2'>
          <div className='md:flex no-wrap'>
            <div className='w-full mx-2'>
              <div className='bg-white p-3'>
                <div className='flex items-center space-x-2 font-bold text-gray-500 leading-8 py-2'>
                  <span className='tracking-wide uppercase text-xl lg:text-3xl'>
                    {userData?.firstName} {userData?.lastName}
                  </span>
                </div>
                <hr className='bg-gray-300 h-[5px]'></hr>
                <form id='editUser' onSubmit={onSubmit}>
                  <div className='text-gray-500'>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Client:</div>
                      </div>
                      <div className='w-full'>
                        <Select
                          id='clientId'
                          name='clientId'
                          onChange={handleChangeSelect}
                          value={formData.clientId ?? ''}
                          sizing='md'
                          disabled={
                            userModel?.userLevelId !== UserLevelNames.Admin
                          }
                        >
                          <option value={''}>(Select)</option>
                          {clients.map((client) => (
                            <option key={client.id} value={client.id}>
                              {client.name}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Name:</div>
                      </div>
                      <div className='flex'>
                        <div className='w-1/2'>
                          <TextInput
                            id='firstName'
                            name='firstName'
                            type='text'
                            placeholder='First Name...'
                            onChange={handleChangeInput}
                            value={formData.firstName ?? ''}
                            sizing='md'
                          />
                        </div>
                        <div className='w-1/2'>
                          <TextInput
                            id='lastName'
                            name='lastName'
                            type='text'
                            placeholder='Last Name...'
                            onChange={handleChangeInput}
                            value={formData.lastName ?? ''}
                            sizing='md'
                          />
                        </div>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Email:</div>
                      </div>
                      <div className='flex'>
                        <div className='w-full'>
                          <TextInput
                            id='emailAddress'
                            name='emailAddress'
                            type='text'
                            placeholder='Email Address...'
                            onChange={handleChangeInput}
                            value={formData.emailAddress ?? ''}
                            sizing='md'
                            required
                          />
                        </div>
                        {emailExists && (
                          <div className='flex w-2/3 text-red-600'>
                            <div className='content-center px-2'>
                              <HiInformationCircle />
                            </div>
                            <div className='content-center'>
                              Email already exists
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Phone:</div>
                      </div>
                      <div className='w-full'>
                        <TextInput
                          id='phoneNumber'
                          name='phoneNumber'
                          type='text'
                          placeholder='Phone...'
                          onChange={handleChangeInput}
                          value={formData.phoneNumber ?? ''}
                          sizing='md'
                        />
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>
                          User Level:
                        </div>
                      </div>
                      <div className='w-full'>
                        <Select
                          id='userLevelId'
                          name='userLevelId'
                          onChange={handleChangeSelect}
                          value={formData.userLevelId ?? 1}
                          sizing='md'
                          disabled={
                            userModel?.userLevelId !== UserLevelNames.Admin
                          }
                        >
                          {userLevels.map((userLevel) => (
                            <option key={userLevel.id} value={userLevel.id}>
                              {userLevel.name}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Bio:</div>
                      </div>
                      <div>
                        <Textarea
                          id='bio'
                          name='bio'
                          placeholder='Bio...'
                          onChange={handleChangeTextArea}
                          value={formData.bio ?? ''}
                          //   sizing='md'
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                </form>
                <div
                  className={`flex ${
                    userData ? 'justify-between' : 'justify-end'
                  } pt-2`}
                >
                  {userData && (
                    <div>
                      <Button
                        className='buttonMain'
                        onClick={() => setShow(true)}
                      >
                        Update Password
                      </Button>
                      <UserUpdatePassword
                        show={show}
                        setShow={setShow}
                        user={userData}
                      />
                    </div>
                  )}
                  <Button
                    disabled={buttonDisabled}
                    type='submit'
                    className='buttonMain'
                    form='editUser'
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
